import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchWorkflowRequest,
  getWorkflowHasUnsavedChanges,
  openModal,
} from 'modules/workflows/ducks';
import { DRAFT_HAS_UNSAVED_CHANGES_MODAL } from 'modules/workflows/constants';

import { BUTTON_VARIANTS } from 'ui/button';
import { ChevronDown } from 'ui/icons';
import { Menu } from 'ui/menu';

import { WorkflowType } from 'modules/workflows/enums';
import { Workflow } from '../../../types';

import { MenuButton } from './styled';
import { createWorkflowTypeOptions } from './utils';

type WorkflowTypeMenuProps = {
  workflow: Pick<Workflow, 'id' | 'workflowType' | 'workflowReference'>;
};

export const WORKFLOW_TYPE_MENU_ID = 'workflow-type-menu';

export const WorkflowTypeMenu = ({ workflow }: WorkflowTypeMenuProps) => {
  const { workflowType, workflowReference } = workflow;
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);

  const handleSelectWorkflowType = useCallback(
    (selected: any) => {
      const [selectedWorkflowType, selectedWorkflowId] = selected.split('-');
      // do not update selection if current workflow version is re-selected
      if (selectedWorkflowType === workflowType || !selectedWorkflowId) return;

      const workflowId = Number(selectedWorkflowId);
      if (hasUnsavedChanges)
        dispatch(
          openModal(DRAFT_HAS_UNSAVED_CHANGES_MODAL, {
            workflowId,
            workflowType: selectedWorkflowType,
          }),
        );
      else dispatch(fetchWorkflowRequest(workflowId, selectedWorkflowType));
    },
    [workflowType, hasUnsavedChanges],
  );

  const options = useMemo(
    () => createWorkflowTypeOptions(workflow, workflowReference),
    [workflow?.id],
  );

  // do not need to display menu if there is only one workflow version
  if (!workflowReference?.id || !workflowReference?.workflowType) return <></>;

  return (
    <div onClick={() => setIsMenuOpen(!isMenuOpen)}>
      <Menu
        handleSelectionChange={handleSelectWorkflowType}
        isOpen={isMenuOpen}
        options={options}
        data-test={WORKFLOW_TYPE_MENU_ID}
      >
        <MenuButton
          variant={BUTTON_VARIANTS.GHOSTINVERTED}
          $workflowType={workflowType as WorkflowType}
        >
          <ChevronDown height={16} width={16} />
        </MenuButton>
      </Menu>
    </div>
  );
};
